/**
 * Utility function for retrying API calls with exponential backoff
 * @param fetchFn The function to retry
 * @param maxRetries Maximum number of retry attempts
 * @param delay Base delay in milliseconds between retries
 * @returns Promise that resolves with the result of the fetch function
 */
export async function retryFetch<T>(
  fetchFn: () => Promise<T>, 
  maxRetries = 3, 
  delay = 1000
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fetchFn();
    } catch (error) {
      console.warn(`API call failed (attempt ${attempt + 1}/${maxRetries}):`, error);
      lastError = error;
      
      // Wait before retrying with exponential backoff
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
      }
    }
  }
  
  throw lastError;
}

/**
 * Check if the user is online
 * @returns Boolean indicating if the user is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Utility function to handle network errors
 * @param error The error to handle
 * @returns A user-friendly error message
 */
export function getNetworkErrorMessage(error: any): string {
  if (!navigator.onLine) {
    return 'You appear to be offline. Please check your internet connection.';
  }
  
  if (error?.message?.includes('Failed to fetch') || error?.message?.includes('NetworkError')) {
    return 'Network error. Please check your connection and try again.';
  }
  
  if (error?.status === 429) {
    return 'Too many requests. Please try again later.';
  }
  
  return error?.message || 'An unexpected error occurred. Please try again.';
}